import React from 'react';
import classnames from 'classnames';
import { CollaboratorAvatar, Link, ShareButton } from 'components';
import {
  ItemMetadata,
  ItemIntegrationIcons,
  FolderLink,
  FileLink,
  AuthLink,
  NodeSubscription,
} from './components';
import { useSetPinState } from 'services/apollo/hooks';
import { useParams } from 'react-router-dom';
import { flags, getFlag } from 'services/developer';
import Icon from 'assets/icons';
import { CheckLabel } from 'components/shared';
import styles from './FeedListItem.module.scss';
import { useNodeState, useFeatureFlagState } from 'context';
import { fileTypeImageByType } from 'assets/img/fileTypes';
import {
  trackingDocumentSelectPrimaryFeed,
  trackingDocumentSelectIntegrationFeed,
} from 'services/analytics';

interface PinProps {
  node: any;
  isPinned?: boolean;
  pins: any;
}

const Pin: React.FC<PinProps> = ({ node, isPinned, pins }) => {
  const { groupId } = useParams();
  const [setPinState] = useSetPinState(node, isPinned ? false : true, groupId, pins);

  return (
    <div
      className={classnames(styles.titleButton, 'pin-' + node.id, isPinned && styles.active)}
      onClick={() => {
        setPinState();
      }}
    >
      <Icon.Pin className={styles.titleIcon} />
    </div>
  );
};

interface FeedListItemProps {
  node?: any;
  collectionType?: string;
  nodeId: string;
  nodeType: string;
  name: string;
  baseUrl: string;
  url: string;
  owner: any;
  collaborators?: any;
  modified: any;
  created: any;
  nodeIcon: string;
  isIntegrationConnected: boolean;
  isSelectable?: boolean;
  checkedChange?: any;
  selectedNodes?: any;
  integrationType: string;
  // For Sharing
  isShareable?: string;
  canShareWith?: any;
  shareTargets?: any;
  // For Pinnable Nodes
  pins?: any;
  isPinnable?: boolean;
  // For tracking
  trackIndex?: number;
  trackFeedType?: string;
  trackTotalItems?: number;
  trackSearchTerm?: string;
  // Subscriptions
  isCached?: boolean;
}

export const FeedListItem: React.FC<FeedListItemProps> = ({
  collectionType = 'Separated',
  nodeId,
  nodeType,
  name,
  baseUrl,
  url,
  owner,
  collaborators,
  modified,
  created,
  nodeIcon,
  isIntegrationConnected,
  isSelectable,
  checkedChange,
  selectedNodes,
  integrationType,
  canShareWith,
  // For Pinnable Nodes
  pins,
  node,
  isPinnable = false,
  // For Sharing
  isShareable = false,
  shareTargets,
  trackIndex,
  trackFeedType,
  trackTotalItems,
  trackSearchTerm,
  //Subscriptions
  isCached = true,
}) => {
  const { openNode } = useNodeState();
  const { groupId } = useParams();
  const { groupsFeatureFlag } = useFeatureFlagState();

  const integrationName = integrationType.slice(0, integrationType.indexOf('@'));
  const containerClasses = classnames(
    {
      [styles[`collectionType--${collectionType}`]]: collectionType,
      [styles.activePopup]: openNode === nodeId ? true : false,
    },
    'pin-item-' + nodeId
  );

  const isPinned = pins?.includes(groupId);

  function trackClick() {
    if (trackFeedType === 'HomeActivityFeed') {
      trackingDocumentSelectPrimaryFeed(
        // @ts-ignore
        trackIndex,
        trackTotalItems,
        nodeType,
        collaborators?.pageInfo?.total
      );
    }

    if (trackFeedType === 'IntegrationFeed') {
      trackingDocumentSelectIntegrationFeed(
        // @ts-ignore
        trackIndex,
        trackTotalItems,
        nodeType,
        collaborators?.pageInfo?.total,
        integrationType
      );
    }

    if (trackFeedType === 'SearchFeed') {
      trackingDocumentSelectIntegrationFeed(
        // @ts-ignore
        trackIndex,
        trackTotalItems,
        nodeType,
        collaborators?.pageInfo?.total,
        trackSearchTerm
      );
    }
  }

  return (
    <div className={containerClasses}>
      <div
        className={classnames(styles[`type-${'feed'}`], styles.item, {
          [styles.selectable]: isSelectable,
        })}
      >
        {isSelectable && (
          <CheckLabel
            type="Flat"
            checked={selectedNodes.includes(nodeId)}
            onChange={(isChecked) => {
              checkedChange(nodeId, nodeType, isChecked);
            }}
          />
        )}

        <div className={styles.iconWrapper}>
          {owner && (
            <div className={styles.owner}>
              <Link to={`/profile/${owner.id}`}>
                <CollaboratorAvatar
                  collaborator={{
                    displayName: owner.name,
                    emailAddress: owner.email,
                    photoURL: owner.avatarUrl,
                  }}
                  hoverEffects="Default"
                />
              </Link>
            </div>
          )}
          <div className={styles.icon}>{fileTypeImageByType(nodeIcon)}</div>
        </div>

        <div className={styles.contentWrapper}>
          <div className={styles.titleWrapper} onClick={trackClick}>
            {isIntegrationConnected && nodeType === 'Folder' && (
              <FolderLink nodeId={nodeId} name={name} />
            )}
            {isIntegrationConnected && nodeType !== 'Folder' && (
              <FileLink url={url} baseUrl={baseUrl} name={name} />
            )}
            {!isIntegrationConnected && (
              <AuthLink nodeName={name} integrationType={integrationType} />
            )}
            {isPinnable && <Pin isPinned={isPinned} node={node} pins={pins} />}
          </div>
          <ItemMetadata
            integrationType={integrationType}
            className={styles.metadata}
            modified={modified}
            created={created}
          />
        </div>

        <div className={styles.collabSourceWrapper}>
          <div className={styles.collaborators}>
            {groupsFeatureFlag && isShareable && isIntegrationConnected ? (
              <ShareButton nodeId={nodeId} shareTargets={shareTargets} />
            ) : null}
            {collaborators?.accessors?.length > 0 && (
              <>
                {collaborators?.accessors?.slice(0, 3).map((collaborator: any, key: number) => {
                  return (
                    <div key={key} className={styles.collaborator}>
                      {collaborator.__typename === 'Group' && collaborator.id && (
                        <Link to={`/group/${collaborator.id}`}>
                          <CollaboratorAvatar
                            collaborator={{
                              displayName: collaborator.groupName,
                            }}
                            emoji={collaborator.emoji}
                            hoverEffects="Default"
                            type="Group"
                          />
                        </Link>
                      )}
                      {collaborator.__typename !== 'Group' && collaborator.id && (
                        <Link to={`/profile/${collaborator.id}`}>
                          <CollaboratorAvatar
                            collaborator={{
                              displayName: collaborator.name,
                              emailAddress: collaborator.email,
                              photoURL: collaborator.avatarUrl,
                            }}
                            hoverEffects="Default"
                          />
                        </Link>
                      )}
                    </div>
                  );
                })}
                {collaborators?.pageInfo?.total > 3 && (
                  <div className={styles.moreCollaborators}>
                    <span className={styles.moreCollaborators__text}>{`+${
                      collaborators?.pageInfo?.total - 3
                    }`}</span>
                  </div>
                )}
              </>
            )}
          </div>
          <ItemIntegrationIcons
            baseUrl={baseUrl}
            className={styles.integrationContainer}
            itemIntegrationName={integrationName}
          />
        </div>
      </div>
      {groupId && !isCached && getFlag(flags.SubscriptionsEnabled) && (
        <NodeSubscription nodeId={nodeId} nodeTitle={name} groupId={groupId} />
      )}
    </div>
  );
};
